import React from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { IdTaskDto, TaskDto } from '@/services/ws-funnel/src/main/resources/rest-api';
import { assignTasks, getSalesContacts } from '@/services/ws-funnel/src/main/resources/rest-api';
import { AsyncSelectField } from '@/js/libs/form';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import type { ModalStateOpen } from '@/js/libs/hooks/useModalState';
import type { SelectionState } from '@/js/libs/hooks/useSelectionState';
import { Button, Modal } from '@/js/libs/ui/src/V2';
import { useLazyRequest } from '@/js/libs/use-request';

import { DEFAULT_PAGE, DROP_DOWN_ENTITY_LIMIT } from 'src/constants';
import { useRequestErrorNotification } from 'src/hooks/useRequestErrorNotification';
import { useSuccessNotification } from 'src/hooks/useSuccessNotification';
import type { FormValues } from './schema';

interface AssignModalProps {
  state: ModalStateOpen<IdTaskDto[]>;
  form: UseFormReturn<FormValues>;
  selection: SelectionState<TaskDto, IdTaskDto>;
  reloadTasks: () => void;
}

export const AssignModal: React.FC<AssignModalProps> = React.memo(function AssignModal({ state, form, selection, reloadTasks }) {
  const [t] = useTranslation(['backend', 'common', 'serviceDashboard', 'messages']);
  const onFail = useRequestErrorNotification();

  const salesContactsRequest = useLazyRequest({
    apiMethod: getSalesContacts,
    onFail,
  });
  const loadSalesContacts = useCallbackRef(async (prefix: string) => {
    const { data } = await salesContactsRequest.load({
      filter: prefix,
      includeInactive: false,
      page: DEFAULT_PAGE,
      limit: DROP_DOWN_ENTITY_LIMIT,
    });

    return data.items.map((contact) => ({
      label: contact.name,
      value: contact.id,
    }));
  });

  const assignRequest = useLazyRequest({
    apiMethod: assignTasks,
    onSuccess: useSuccessNotification(t('serviceDashboard:TASK_WAS_ASSIGNED')),
    onFail,
  });

  const {
    control,
    formState: { isSubmitting },
  } = form;

  const onSubmit = useCallbackRef(async (values: FormValues) => {
    if (state.payload && values.assignee) {
      await assignRequest.load({ taskIds: state.payload, assigneeId: values.assignee.value.toString() });
      selection.deselectAll();
      reloadTasks();
      state.close();
    }
  });

  return (
    <Modal state={state} title={t('serviceDashboard:ADD_ASSIGNEE')}>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Modal.Body data-testid='add-assignee-modal'>
            <AsyncSelectField
              control={control}
              name='assignee'
              loadOptions={loadSalesContacts}
              label={t('serviceDashboard:ASSIGNEE')}
              placeholder={t('common:ENTER')}
              defaultOptions
              isClearable
              required
            />
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={state.close} ghost disabled={isSubmitting} data-testid='cancel-button'>
              {t('common:CANCEL')}
            </Button>
            <Button primary type='submit' disabled={isSubmitting} isLoading={isSubmitting} data-testid='submit-button'>
              {t('common:ADD')}
            </Button>
          </Modal.Footer>
        </form>
      </FormProvider>
    </Modal>
  );
});

export { type FormValues as AssignModalFormValues, createSchema as assignModalCreateSchema } from './schema';
